/**
 * 角色與存取層級常數定義（前端 SSOT，僅 UI 用）
 *
 * 鏡像後端 backend/src/config/accessControl.js：
 * - RESTRICTED_VIEWER_ROLES / canViewRestricted（可見特殊資料的角色）
 * - ACCESS_LEVELS（文件存取層級）
 *
 * 注意：權限的真實判斷在後端（每請求查 DB role）。
 * 本檔僅供前端顯示（角色標籤、選單、chip/badge），不作安全邊界。
 *
 * @see backend/src/config/accessControl.js
 * @see frontend/src/contexts/AuthContext.jsx（hasRole 權限層級）
 */

// ============================================================
// 角色（Role）
// ============================================================

export const ROLES = Object.freeze({
  USER: 'user',
  ADVANCED: 'advanced',
  ADMIN: 'admin',
  RESEARCH: 'research', // 研究者：比照 user，但可檢視特殊資料
});

// 所有合法角色（鏡像後端 ALL_ROLES）
export const ALL_ROLES = ['user', 'advanced', 'admin', 'research'];

// 角色顯示名稱
export const ROLE_LABELS = Object.freeze({
  user: '一般使用者',
  advanced: '進階使用者',
  admin: '管理員',
  research: '研究者',
});

// 角色對應 MUI Chip 顏色
export const ROLE_COLORS = Object.freeze({
  user: 'default',
  advanced: 'warning',
  admin: 'error',
  research: 'info', // 研究者用 info 色，與其他角色區隔
});

// 角色層級（鏡像 AuthContext roleHierarchy；research 比照 user）
export const ROLE_LEVELS = Object.freeze({
  user: 1,
  research: 1,
  advanced: 2,
  admin: 3,
});

// 角色選單選項（順序：user → advanced → research → admin，
// research 緊鄰 admin 反映兩者皆可檢視特殊資料）
export const ROLE_OPTIONS = [
  { value: 'user', label: '一般使用者', description: '可搜尋、引用' },
  { value: 'advanced', label: '進階使用者', description: '可搜尋、引用、上傳' },
  { value: 'research', label: '研究者', description: '可搜尋、引用、檢視特殊資料' },
  { value: 'admin', label: '管理員', description: '完整系統權限' },
];

// 取得角色顯示名稱（未知/空值 fallback「一般使用者」）
export const getRoleLabel = (role) => ROLE_LABELS[role] || ROLE_LABELS.user;

// 取得角色 Chip 顏色（未知/空值 fallback default）
export const getRoleColor = (role) => ROLE_COLORS[role] || ROLE_COLORS.user;

// ============================================================
// 存取層級（access_level）
// ============================================================

// 可檢視特殊資料（restricted）的角色（鏡像後端 RESTRICTED_VIEWER_ROLES）
export const RESTRICTED_VIEWER_ROLES = ['research', 'admin'];

// 是否可檢視特殊資料（allowlist，未知/空值 fail-safe → false）
export const canViewRestricted = (role) => RESTRICTED_VIEWER_ROLES.includes(role);

export const ACCESS_LEVELS = Object.freeze({
  PUBLIC: 'public',
  RESTRICTED: 'restricted',
});

// 特殊資料 chip/badge 顯示文字
export const RESTRICTED_CHIP_LABEL = '特殊資料';

// 存取層級選單選項（admin 設定文件密級用）
export const ACCESS_LEVEL_OPTIONS = [
  { value: 'public', label: '一般資料' },
  { value: 'restricted', label: '特殊資料' },
];

// 是否為特殊資料（嚴格等於 'restricted'；undefined/null/其他皆 false）
export const isRestricted = (accessLevel) => accessLevel === 'restricted';
